import { useEffect, useState, RefObject } from 'react';

import { useSelector } from 'react-redux';

import { shouldRenderBanner } from '@atlassian/browser-storage-controls';

import { getSiteMessageBanner } from 'src/selectors/global-selectors';
import { SiteMessage } from 'src/types/site-message';

export const useSiteBannerMessage = (): SiteMessage | null => {
  return useSelector(getSiteMessageBanner);
};

/**
 * Hook to check if consent banner will render content
 * Performs async check using util provided by @atlassian/browser-storage-controls
 * @returns boolean indicating if consent banner has content to display
 */
export const useHasConsentBanner = (): boolean => {
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkShouldRender = async () => {
      try {
        const shouldRender = await shouldRenderBanner();
        if (isMounted) {
          setHasContent(shouldRender);
        }
      } catch {
        // Default to false to prevent layout issues
        if (isMounted) {
          setHasContent(false);
        }
      }
    };

    checkShouldRender();
    return () => {
      isMounted = false;
    };
  }, []);

  return hasContent;
};

/**
 * hook to track an element's height using ResizeObserver
 * @param elementRef - ref to the element to measure (usually wrapper)
 * @param enabled - whether to actively track height (default: true)
 * @returns current height in px
 */
export const useElementHeight = (
  elementRef: RefObject<HTMLElement>,
  enabled: boolean = true
): number => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!enabled || !elementRef.current) {
      return;
    }

    const element = elementRef.current;

    const safeSetHeight = (newHeight: number | undefined) => {
      setHeight(Math.max(0, newHeight || 0));
    };

    const observer = new ResizeObserver(entries => {
      try {
        const contentHeight = entries[0]?.contentRect?.height;
        safeSetHeight(contentHeight);
      } catch {
        // fallback to direct measurement
        safeSetHeight(element?.offsetHeight);
      }
    });

    try {
      observer.observe(element);
    } catch {
      // if observer fails, measure once immediately
      safeSetHeight(element.offsetHeight);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      try {
        observer.unobserve(element);
      } catch {
        // Safe to ignore cleanup errors
      }
    };
  }, [enabled, elementRef]);

  return height;
};
